import { LitElement, css, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { teknikTokens } from './teknik-tokens.js';
import '../components/header.js';

/**
 * The page shell.
 *
 * Before this existed, 23 of 42 pages rendered their own <app-header>, 20
 * declared their own <h1>, and every one picked a different header offset —
 * 90px, 100px, 8px, 2px, calc(48px + 2.5rem). That inconsistency was the site's
 * most visible problem, and it was structural rather than cosmetic.
 *
 * Usage:
 *
 *     render() {
 *       return html`
 *         <tk-page eyebrow="Minecraft" heading="Your servers"
 *                  sub="Servers on your plan.">
 *           …page content…
 *         </tk-page>`;
 *     }
 *
 * <app-header> hydrates itself from /api/auth/status, so no auth props need
 * threading through. Content is slotted, which means it stays in the calling
 * page's shadow tree — so that page imports `tkPrimitives` for .tk-panel etc.
 *
 * The page head follows app-construction.ts exactly: a mono eyebrow with a
 * hairline running to the right margin, a Plex Cond headline, then a muted sub.
 */
@customElement('tk-page')
export class TkPage extends LitElement {
  /** Mono uppercase kicker above the headline. Omit for a bare shell. */
  @property({ type: String }) eyebrow = '';
  /** The page's h1. Omit to render no page head at all. */
  @property({ type: String }) heading = '';
  /** One muted line under the headline. */
  @property({ type: String }) sub = '';
  /** Dashboards and tables want more room than prose does. */
  @property({ type: Boolean }) wide = false;
  /** Full-bleed: no wrap padding or max-width (console, file browser). */
  @property({ type: Boolean }) flush = false;

  static styles = [
    teknikTokens,
    css`
      :host {
        display: block;
        font-family: var(--font-family);
        color: var(--tk-text);
      }

      @keyframes tk-rise {
        from { opacity: 0; transform: translateY(8px); }
        to   { opacity: 1; transform: none; }
      }

      main {
        background: var(--tk-bg);
        min-height: 100vh;
        /* 48px fixed header + breathing room. One value, one place. */
        padding: calc(48px + 3rem) 0 7rem;
        box-sizing: border-box;
      }

      .wrap {
        width: 100%;
        max-width: 1080px;
        margin: 0 auto;
        padding: 0 24px;
        box-sizing: border-box;
      }
      :host([wide]) .wrap { max-width: 1320px; }
      :host([flush]) .wrap { max-width: none; padding: 0; }
      :host([flush]) main { padding-bottom: 0; }

      .head {
        margin-bottom: 2.25rem;
        animation: tk-rise 0.4s ease both;
      }

      .eyebrow {
        display: flex;
        align-items: center;
        gap: 0.9rem;
        margin-bottom: 1.1rem;
        font-family: var(--font-mono);
        font-size: 11px;
        font-weight: 500;
        letter-spacing: 0.16em;
        text-transform: uppercase;
        color: var(--tk-accent);
      }
      .eyebrow::after {
        content: '';
        flex: 1;
        height: 1px;
        background: var(--tk-border);
      }

      h1 {
        font-family: var(--font-display);
        font-weight: 700;
        font-size: clamp(2rem, 4.5vw, 2.75rem);
        line-height: 1.06;
        letter-spacing: -0.02em;
        margin: 0;
        text-wrap: balance;
      }

      .sub {
        margin: 0.85rem 0 0;
        font-size: 1.0625rem;
        line-height: 1.6;
        color: var(--tk-muted);
        max-width: 60ch;
      }

      @media (max-width: 760px) {
        main { padding-top: calc(48px + 2rem); }
        .wrap { padding: 0 18px; }
        :host([flush]) .wrap { padding: 0; }
        .head { margin-bottom: 1.75rem; }
      }

      @media (prefers-reduced-motion: reduce) {
        .head { animation: none; }
      }
    `,
  ];

  render() {
    return html`
      <app-header></app-header>
      <main>
        <div class="wrap">
          ${this.heading
            ? html`
                <header class="head">
                  ${this.eyebrow ? html`<div class="eyebrow">${this.eyebrow}</div>` : ''}
                  <h1>${this.heading}</h1>
                  ${this.sub ? html`<p class="sub">${this.sub}</p>` : ''}
                </header>
              `
            : ''}
          <slot></slot>
        </div>
      </main>
    `;
  }
}
